import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, ShieldCheck, UserRound, Globe2, Crown,
  Activity, Hexagon, Target, TrendingUp, Quote, Home as HomeIcon, Utensils,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";
import { TESTIMONIALS } from "@/data/content";

// Imagery — cinematic chiaroscuro across all subjects
const IMG = {
  heroBowl:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/2mvcx5p5_IMG_2091.png",
  cardAdvisory:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/hzqinar3_IMG_2099.jpeg",
  cardYacht:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/7n103p21_IMG_2105.png",
  cardHospitality:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/x8bsamln_IMG_2103.png",
  caseClient:
    "https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=1200&q=85",
  caseYacht:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/rgjia159_IMG_2100.png",
  caseHospitality:
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/nvakaqkb_IMG_2114.png",
  greenSmoothie:
    "https://images.unsplash.com/photo-1610970881699-44a5587cabec?auto=format&fit=crop&w=900&q=85",
  applyBg:
    "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?auto=format&fit=crop&w=1600&q=85",
};

// Static visual data (icons + images) — text comes from translations
const TRUST_ICONS = [ShieldCheck, UserRound, Globe2, Crown];
const WHATWEDO_ICONS = [UserRound, HomeIcon, Utensils];
const WHATWEDO_IMGS = [IMG.cardAdvisory, IMG.cardYacht, IMG.cardHospitality];
const FRAMEWORK_ICONS = [Activity, Hexagon, Target, TrendingUp];
const CASE_IMGS = [IMG.caseClient, IMG.caseYacht, IMG.caseHospitality];

export default function Home() {
  const { t } = useLang();

  return (
    <div data-testid="page-home" className="bg-[#070E13] text-[#F4EEE8]">
      {/* HERO — stacked on mobile, split layout (text left 55%, photo right 45%) on lg+ */}
      <section className="relative bg-[#070E13] lg:min-h-screen lg:overflow-hidden">
        {/* DESKTOP: photo absolute right 58%; MOBILE: photo flows below text */}
        <div className="hidden lg:block absolute inset-0 lg:inset-y-0 lg:right-0 lg:left-[38%]">
          <img
            src={IMG.heroBowl}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: "center center", filter: "brightness(0.85) contrast(1.08) saturate(1.05)" }}
            loading="eager"
          />
          {/* Cinematic left-fade — deeper, smoother bleed into dark text column */}
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, #070E13 0%, rgba(7,14,19,0.95) 22%, rgba(7,14,19,0.55) 42%, rgba(7,14,19,0.18) 60%, transparent 75%)" }} />
          {/* Vertical atmospheric darkening — top and bottom */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/55 via-transparent to-[#070E13]/65" />
          {/* Warm golden chiaroscuro wash */}
          <div className="absolute inset-0 mix-blend-overlay opacity-30" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.28), rgba(7,14,19,0.55))" }} />
          {/* Inner vignette — frames the subject */}
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 200px 35px rgba(7,14,19,0.55), inset 0 0 80px 10px rgba(199,162,108,0.08)" }} />
        </div>

        {/* Text content — relative on mobile (flows in document), absolute-positioned grid on lg */}
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 pt-32 pb-12 lg:pb-24 lg:min-h-[92vh] lg:grid lg:items-center">
          <div className="lg:w-[55%] xl:w-[52%]">
            <Reveal>
              <p className="overline gold-text">{t.home.eyebrow}</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="h-editorial mt-7 text-6xl sm:text-7xl lg:text-[5.5rem] xl:text-[6rem] leading-[0.98] lg:leading-[1.04]">
                {t.home.heroTitle}
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-9 max-w-xl text-lg lg:text-xl text-[#D6D1C9] leading-relaxed">
                {t.home.heroLead}
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-10 lg:mt-12 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="cta-premium inline-flex items-center justify-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-5 lg:py-4 text-xs uppercase tracking-[0.24em] font-medium"
                >
                  {t.home.ctaPrimary} <ArrowRight size={16} />
                </Link>
                <Link
                  to="/services"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 border border-[#F4EEE8]/35 text-[#F4EEE8] px-9 py-5 lg:py-4 text-xs uppercase tracking-[0.24em] hover:border-[#C7A26C] hover:text-[#C7A26C] transition-colors"
                >
                  {t.home.ctaSecondary}
                </Link>
              </div>
            </Reveal>
            {/* Trust line — visible on lg+ inside hero text column (matches old design) */}
            <Reveal delay={480} className="hidden lg:block">
              <p className="mt-14 flex items-center gap-2 text-xs text-[#D6D1C9]">
                <ShieldCheck size={14} className="text-[#C7A26C]" />
                {t.home.heroTrust}
              </p>
            </Reveal>
          </div>
        </div>

        {/* MOBILE-ONLY: full-width photo flowing below text */}
        <Reveal delay={480} className="lg:hidden mt-2">
          <div className="relative w-full h-[55vh] sm:h-[65vh] overflow-hidden">
            <img
              src={IMG.heroBowl}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: "center center", filter: "brightness(0.85) contrast(1.05) saturate(1.05)" }}
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/40 via-transparent to-[#070E13]/30" />
            <div className="absolute inset-0 mix-blend-overlay opacity-35" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.18), rgba(7,14,19,0.4))" }} />
            <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 200px 30px rgba(7,14,19,0.55)" }} />
          </div>
        </Reveal>

        {/* MOBILE-ONLY: trust line below photo */}
        <div className="lg:hidden border-y border-[#C7A26C]/15 bg-[#070E13]">
          <div className="px-6 py-7 flex items-center justify-center gap-3 text-center">
            <ShieldCheck size={14} className="text-[#C7A26C] shrink-0" />
            <p className="text-xs text-[#D6D1C9]">
              {t.home.heroTrust}
            </p>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-[#C7A26C]/20 bg-[#0C1821]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#C7A26C]/15">
          {t.home.trustStrip.map((it, i) => {
            const Icon = TRUST_ICONS[i];
            return (
              <div key={i} className="relative p-7 lg:p-9 flex items-start gap-4 group hover:bg-[#070E13]/40 transition-colors">
                <div className="w-11 h-11 rounded-full border border-[#C7A26C]/50 flex items-center justify-center shrink-0 group-hover:border-[#C7A26C] transition-colors">
                  <Icon size={16} className="text-[#C7A26C]" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="overline text-[#F4EEE8] leading-snug">{it.t}</p>
                  <p className="text-xs text-[#C7A26C]/80 mt-2">{it.s}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline gold-text">{t.home.whatWeDo.eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5 max-w-3xl">
              {t.home.whatWeDo.title}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.home.whatWeDo.items.map((c, i) => {
              const Icon = WHATWEDO_ICONS[i];
              const img = WHATWEDO_IMGS[i];
              return (
                <Reveal key={i} delay={i * 100} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/50 transition-colors duration-500 group">
                  <GoldCorners size={14} inset={-1} />
                  <div className="relative h-44 overflow-hidden">
                    <img src={img} alt={c.t} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0C1821] via-[#0C1821]/40 to-[#0C1821]/10" />
                    <div className="absolute inset-0 mix-blend-overlay opacity-60" style={{ background: "linear-gradient(135deg, rgba(201,166,107,0.18), rgba(7,43,58,0.4))" }} />
                    <div className="absolute bottom-3 left-3 w-12 h-12 rounded-full bg-[#070E13] border border-[#C7A26C]/60 flex items-center justify-center shadow-[0_0_0_1px_rgba(201,166,107,0.15)]">
                      <Icon size={18} className="text-[#C7A26C]" strokeWidth={1.4} />
                    </div>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl">{c.t}</h3>
                    <p className="mt-3 text-sm text-[#D6D1C9] leading-relaxed">{c.d}</p>
                    <Link to="/services" className="inline-flex items-center gap-2 mt-6 text-[#C7A26C] text-xs uppercase tracking-[0.2em] link-underline">
                      {t.home.whatWeDo.cardCta} <ArrowRight size={12} />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRECISION LONGEVITY ARCHITECTURE — framework */}
      <section className="bg-[#0C1821] py-24 sm:py-32 border-y border-[#C7A26C]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-4">
            <p className="overline gold-text">{t.home.framework.eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-5">
              {t.home.framework.title}<sup className="text-[0.4em] ms-1 align-super">™</sup>
            </h2>
            <p className="mt-6 text-[#D6D1C9] leading-relaxed">
              {t.home.framework.lead}
            </p>
            <Link to="/method" className="cta-premium inline-flex items-center gap-2 mt-8 border border-[#C7A26C]/60 text-[#C7A26C] px-6 py-3 text-xs uppercase tracking-[0.2em] hover:bg-[#C7A26C] hover:text-[#070E13] transition-colors">
              {t.home.framework.cta}
            </Link>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-8">
            <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="hidden lg:block absolute top-7 left-[12.5%] right-[12.5%] h-px border-t border-dashed border-[#C7A26C]/40" />
              {t.home.framework.steps.map((s, i) => {
                const Icon = FRAMEWORK_ICONS[i];
                return (
                  <div key={i} className="relative text-center z-10">
                    <div className="w-14 h-14 mx-auto rounded-full border border-[#C7A26C]/50 bg-[#070E13] flex items-center justify-center">
                      <Icon size={22} className="text-[#C7A26C]" strokeWidth={1.4} />
                    </div>
                    <p className="text-xs gold-text tracking-[0.2em] uppercase mt-5">{s.n}</p>
                    <p className="font-serif text-xl mt-2">{s.t}</p>
                    <p className="text-xs text-[#D6D1C9] mt-2 leading-relaxed">{s.d}</p>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CASE STUDIES — Proven in demanding environments */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="flex items-end justify-between gap-6 flex-wrap">
            <Reveal>
              <p className="overline gold-text">{t.home.caseStudies.eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5 max-w-3xl">
                {t.home.caseStudies.title}
              </h2>
            </Reveal>
            <Link to="/services" className="text-xs uppercase tracking-[0.2em] text-[#C7A26C] link-underline">
              {t.home.caseStudies.viewAll} →
            </Link>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.home.caseStudies.items.map((cs, i) => (
              <Reveal key={i} delay={i * 90} className="relative bg-[#0C1821] border border-[#C7A26C]/20 hover:border-[#C7A26C]/50 transition-colors duration-500 group">
                <GoldCorners size={14} inset={-1} />
                <div className="relative h-44 overflow-hidden">
                  <img src={CASE_IMGS[i]} alt={cs.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1821] via-[#0C1821]/30 to-transparent" />
                  <div className="absolute inset-0 mix-blend-overlay opacity-50" style={{ background: "linear-gradient(135deg, rgba(201,166,107,0.20), rgba(7,43,58,0.35))" }} />
                </div>
                <div className="p-7">
                  <p className="overline text-[#C7A26C]">{cs.label}</p>
                  <p className="text-sm text-[#D6D1C9] mt-4 leading-relaxed">{cs.d}</p>
                  <Link to="/services" className="inline-flex items-center gap-2 mt-5 text-[#F4EEE8] text-xs uppercase tracking-[0.2em] link-underline">
                    {t.home.caseStudies.readMore} <ArrowRight size={12} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS + PULL QUOTE */}
      <section className="bg-[#0C1821] border-y border-[#C7A26C]/10 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-7">
            <p className="overline gold-text">{t.home.statsBlock.eyebrow}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mt-8">
              {t.home.statsBlock.items.map((s, i) => (
                <div key={i}>
                  <p className="font-serif text-5xl gold-text">{s.n}</p>
                  <p className="text-xs text-[#D6D1C9] mt-2 leading-tight">{s.l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-5 relative">
            <div className="bg-[#070E13] p-8 border border-[#C7A26C]/30 relative">
              <GoldCorners size={12} inset={-1} />
              <Quote size={28} className="text-[#C7A26C] mb-4" strokeWidth={1.5} />
              <p className="font-serif italic text-lg text-[#F4EEE8]/90 leading-relaxed">
                {t.home.quote.text}
              </p>
              <p className="overline text-[#C7A26C] mt-6">{t.home.quote.by}</p>
              <img
                src={IMG.greenSmoothie}
                alt=""
                className="absolute -top-6 -right-6 w-28 h-28 object-cover rounded-full border-4 border-[#070E13] hidden md:block"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* INSTITUTIONAL TEASER */}
      <section className="relative bg-[#070E13] py-24 sm:py-32 overflow-hidden border-y border-[#C7A26C]/15">
        <div className="absolute inset-0 motif-grid opacity-40" />
        <div className="absolute inset-0 motif-hex opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#070E13]/50 to-[#070E13]" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 lg:px-12 text-center">
          <Reveal>
            <p className="overline gold-text">{t.home.institutional.eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl lg:text-6xl mt-5">
              {t.home.institutional.title}
            </h2>
            <p className="mt-8 text-[#D6D1C9] text-lg leading-relaxed max-w-3xl mx-auto">
              {t.home.institutional.lead}
            </p>
            <Link
              to="/institutional"
              data-testid="home-institutional-cta"
              className="cta-premium inline-flex items-center gap-2 mt-10 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-4 text-xs uppercase tracking-[0.24em] font-medium"
            >
              {t.home.institutional.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>


      {/* APPLY NOW CTA */}
      <section className="relative overflow-hidden">
        <img src={IMG.applyBg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#070E13] via-[#070E13]/95 to-[#070E13]/60" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 py-24 sm:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="overline gold-text">{t.home.applyCta.eyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5">
                {t.home.applyCta.title}
              </h2>
              <p className="mt-6 max-w-xl text-[#D6D1C9] leading-relaxed">
                {t.home.applyCta.lead}
              </p>
            </Reveal>
          </div>
          <Reveal delay={200} className="lg:col-span-5 lg:text-end">
            <Link
              to="/contact"
              data-testid="home-closing-cta"
              className="cta-premium inline-flex items-center gap-2 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] px-9 py-5 text-xs uppercase tracking-[0.22em] font-medium"
            >
              {t.home.applyCta.cta} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      <section className="bg-[#0C1821] py-20 border-t border-[#C7A26C]/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 3).map((s, i) => (
              <Reveal key={s.id} delay={i * 80} data-testid={`home-story-${i}`} className="p-7 border-s border-[#C7A26C]/40">
                <p className="font-serif italic text-base leading-relaxed text-[#F4EEE8]/90">
                  "{s.quote.en}"
                </p>
                <p className="mt-5 overline text-[#C7A26C]">— {s.role}</p>
              </Reveal>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/testimonials" className="inline-flex items-center gap-2 text-[#C7A26C] text-xs uppercase tracking-[0.2em] link-underline">
              {t.home.testimonialsCta} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
